'use client';

import { useEffect } from 'react';

import { useEventCallback } from './useEventCallback';

export interface GlobalHotkeyOptions {
  /** Compared case-insensitively against `event.key`, e.g. 'k'. */
  key: string;
  /** Requires the platform command modifier (`metaKey` on mac, `ctrlKey` elsewhere). */
  meta?: boolean;
  shift?: boolean;
  /** Defaults to true. */
  enabled?: boolean;
  /** Skips the hotkey while the user is typing in an input, textarea or contenteditable. Defaults to true. */
  ignoreInputs?: boolean;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    target.closest('[contenteditable="true"]') !== null ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA'
  );
}

/**
 * Binds a window-level keyboard shortcut. Calls `preventDefault` when it fires,
 * so the browser's own shortcut (e.g. Cmd+K in Firefox) does not also run.
 */
export function useGlobalHotkey(
  { key, meta = false, shift = false, enabled = true, ignoreInputs = true }: GlobalHotkeyOptions,
  handler: (event: KeyboardEvent) => void
): void {
  const stableHandler = useEventCallback(handler);

  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== key.toLowerCase()) return;

      const hasCommandModifier = event.metaKey || event.ctrlKey;
      if (meta !== hasCommandModifier) return;
      if (shift !== event.shiftKey) return;
      if (ignoreInputs && isTypingTarget(event.target)) return;

      event.preventDefault();
      stableHandler(event);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [key, meta, shift, enabled, ignoreInputs, stableHandler]);
}
